import React, { useState, useEffect, useContext, createContext } from 'react';
import { useQuery } from '@apollo/react-hooks';
import moment from 'moment';

import { OrganizationContext } from '../../components/organization-select/withOrganization';

import eventsQuery from '../reports/custom-event-filter-modal/customEvents.gql';

import { FilterConstants } from './FilterConstants';

export const FiltersContext = createContext();

/**
 * Calculates the next upcoming timeslot from a list of events.
 * Returns the chronologically earliest timeslot that is in the future.
 *
 * @param {Array} events - Array of event objects with nextFutureTimeSlot
 * @returns {Object|undefined} The earliest upcoming timeslot, or undefined if none exist
 */
export function calculateNextTimeslot(events) {
  const upcomingTimeslots = events
    .map((event) => event.nextFutureTimeSlot)
    .filter((timeslot) => {
      const nextTimeslotStamp = timeslot && timeslot.startStamp;
      const currentStamp = moment.utc().unix();
      return nextTimeslotStamp && nextTimeslotStamp >= currentStamp;
    });

  if (upcomingTimeslots.length > 0) {
    // Sort by startStamp ascending and take the first (earliest)
    upcomingTimeslots.sort((a, b) => a.startStamp - b.startStamp);
    return upcomingTimeslots[0];
  }
  return undefined;
}

/**
 * Whether a dashboard request can omit its event ID list.
 *
 * A membership whose scope is the whole account -- the current user, or a MUM
 * membership with all_listings -- is authorized for every event, so web's MUM
 * check resolves the scope from the membership and the list is redundant. Some
 * hosts have thousands of events, and enumerating them form-encodes past the
 * request body limit (UNI-8648).
 *
 * A narrowed selection is a real filter rather than a scope, so it is always
 * sent, as is the list for a membership scoped to specific listings.
 *
 * @param {Object} membership - The selected membership
 * @param {number} selectedEventCount - Events surviving the user's selection
 * @param {number} selectableEventCount - Events the membership could select from
 * @returns {boolean} True when the request can omit event IDs
 */
export function canOmitEventIds(membership, selectedEventCount, selectableEventCount) {
  if (!membership) return false;
  if (!membership.is_current_user && !membership.all_listings) return false;
  return selectedEventCount === selectableEventCount;
}

export function withFilters(Component) {
  return (props) => {
    const { selectedMembership } = useContext(OrganizationContext);

    const [ events, setEvents ] = useState([]);

    const [ currencyAndPermissionFilteredEvents, setCurrencyAndPermissionFilteredEvents ] = useState([]);

    const allUserSelectableEventIds = currencyAndPermissionFilteredEvents.map(event => event.id)
    const [ userSelectedEventIds, setUserSelectedEventIds ] = useState(allUserSelectableEventIds);
    
    const [ currencies, setCurrencies ] = useState([]);

    const [ nextTimeslot, setNextTimeslot ] = useState(undefined);
    const [ nextTimeslotLoading, setNextTimeslotLoading ] = useState(true);

    const [ dataError, setDataError ] = useState(null);
    const [ dataLoading, setDataLoading ] = useState(true);
    const [ isEmpty, setIsEmpty ] = useState(false);

    const hostId = selectedMembership.owner.id;

    const fetchMoreEvents = (totalCount, limit, eventsList, fetchMore) => {
      if (eventsList.length === totalCount) {
        return;
      };

      const offset = eventsList.length;
      fetchMore({
        variables: { offset, limit },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            ...prev,
            host: {
              ...prev.host,
              events: {
                ...prev.host.events,
                nodes: [
                  ...prev.host.events.nodes,
                  ...fetchMoreResult.host.events.nodes
                ]
              },
            },
          };
        }
      });
    };

    const { data, loading, error, fetchMore, refetch } = useQuery(eventsQuery, {
      variables: { hostId, limit: FilterConstants.EVENTS_QUERY_LIMIT },
    });

    const [ selectedFilters, setSelectedFilters ] = useState({
      currency: '',
      eventIds: [],
      // purchase date = date of order
      minPurchaseDate: moment().subtract(30, 'day').startOf('day').utcOffset(0, true),
      maxPurchaseDate: moment().endOf('day').utcOffset(0, true),
      minAttendanceDate: '',
      maxAttendanceDate: '',
      exactAttendanceStartTimeSecondsOfDay: '', // Specific timeslot, start time.
      exactAttendanceEndTimeSecondsOfDay: '', // Specific timeslot, end time as calculated by duration.
      minAttendanceStartTimeSecondsOfDay: '', // Range of timeslots, start time.
      maxAttendanceStartTimeSecondsOfDay: '', // Range of timeslots, end time.
      dataType: FilterConstants.TICKETS_AND_ADD_ONS
    });

    // Refetch event data when selected membership changes.
    useEffect(() => {
      setIsEmpty(false);
      setNextTimeslotLoading(true);
      // Clear event IDs to prevent stale IDs from the previous membership being sent
      // before the filtering effect recomputes them for the new host.
      setSelectedFilters((prev) => ({
        ...prev,
        eventIds: []
      }));
      refetch({ hostId: selectedMembership.owner.id })
    }, [selectedMembership, refetch]);

    useEffect(() => {
      setDataError(error);
      setDataLoading(loading);
    }, [error, loading]);

    useEffect(() => {
      if (!loading && data) {
        const { eventCurrencies } = data.host;

        // Set events.
        const postedOrExpiredEvents = data.host.events.nodes.filter((event) => {
          return event.state === 'POSTED' || event.state === 'EXPIRED';
        });
        setEvents(postedOrExpiredEvents);

        // Set currencies.
        setCurrencies(eventCurrencies);
      }
    }, [loading, data]);

    // Set default currency immediately when data loads or validate existing currency
    useEffect(() => {
      if (currencies.length > 0) {
        const defaultCurrency = currencies[0];

        // Set default if no currency selected, or reset if selected currency is invalid
        if (!selectedFilters.currency || !currencies.includes(selectedFilters.currency)) {
          setSelectedFilters((prev) => ({
            ...prev,
            currency: defaultCurrency,
            eventIds: []
          }));
        }
      }
    // We intentionally read selectedFilters.currency without including it in deps.
    // This is safe because we use the function form of setSelectedFilters which reads
    // the latest state. Adding selectedFilters to deps would cause an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currencies]);

    useEffect(() => {
      if (!loading && data) {
        const { totalCount } = data.host.events;
        const eventsList = data.host.events.nodes;
        fetchMoreEvents(totalCount, FilterConstants.EVENTS_QUERY_LIMIT, eventsList, fetchMore);
      };
    }, [loading, data, fetchMore]);

    useEffect(() => {
      // Ignore changes until data is not loading.
      if (loading || !data) {
        return;
      }

      // Ignore changes until all host events have loaded.
      if (data
        && data.host
        && data.host.events
        && data.host.events.nodes.length !== data.host.events.totalCount) {
        return;
      };

      // Calculate the events filtered by currency and permission (for MUM users).
      const filteredEvents = events.filter((event) => {
        if (!selectedFilters.currency || !selectedMembership) return false;
        const currencyCode = selectedFilters.currency.substring(0, 3); // TODO: Trim?
        const hasPermission = selectedMembership.all_listings 
          || (selectedMembership.event_ids && selectedMembership.event_ids.includes(event.id))
        return event.currency === currencyCode && hasPermission;
      });

      // Set the list of events filtered by currency and permission.
      setCurrencyAndPermissionFilteredEvents(filteredEvents);

      // Apply the user selections to build a final list of filtered events.
      const finalFilteredEvents = filteredEvents.filter((event) => {
        return userSelectedEventIds.length === 0 || userSelectedEventIds.includes(event.id);
      });
      let finalFilteredEventIds = finalFilteredEvents.map(event => event.id);

      // For MUM users, we can only show data if there are valid events selected.
      setIsEmpty(finalFilteredEventIds.length === 0);

      // Calculate the next upcoming timeslot (chronologically earliest across selected events).
      // Note: Backend returns events in natural order, NOT sorted by nextFutureTimeSlot,
      // so we must sort on the frontend to find the actual earliest timeslot.
      const calculatedTimeslot = calculateNextTimeslot(finalFilteredEvents);
      setNextTimeslot(calculatedTimeslot);
      setNextTimeslotLoading(false);

      // Whole-account scope is resolved server-side from the membership, so the list is redundant.
      if (canOmitEventIds(selectedMembership, finalFilteredEvents.length, filteredEvents.length)) {
        finalFilteredEventIds = [];
      }

      // Early-out if the filtered event IDs haven't changed.
      if (JSON.stringify(finalFilteredEventIds) === JSON.stringify(selectedFilters.eventIds)) {
        return;
      }

      // Set the eventIds to filter for sending to Superset.
      setSelectedFilters({
        ...selectedFilters,
        eventIds: finalFilteredEventIds
      });
    }, [data, loading, events, userSelectedEventIds, selectedFilters, selectedMembership]);

    // Readiness signal for consumers that must wait for the full event list before
    // rendering. An empty event ID list is a valid steady state, so it cannot be used.
    const eventsFullyLoaded = Boolean(
      !loading
      && data
      && data.host
      && data.host.events
      && data.host.events.nodes.length === data.host.events.totalCount
    );

    return (
      <FiltersContext.Provider value={{
        currencyAndPermissionFilteredEvents,
        events,
        nextTimeslot,
        nextTimeslotLoading,

        currencies,

        dataError,
        dataLoading,
        eventsFullyLoaded,
        isEmpty,

        selectedFilters, setSelectedFilters,
      
        userSelectedEventIds, setUserSelectedEventIds,
        allUserSelectableEventIds}}>
        <Component {...props} />
      </FiltersContext.Provider>
    )
  };
}
